/**
 * SettingsScreen — configuration personnelle de UNIQUE.
 *
 * Permet à chaque utilisateur de paramétrer :
 *   - Les délais d'escalade (en jours) pour chacun des 4 quadrants
 *   - Les seuils d'urgence et d'importance (en heures d'échéance)
 *   - Les coordonnées de son supérieur hiérarchique N+1
 *   - Les mots-clés qui déclenchent "urgent"/"important" dans les emails
 *   - La durée par défaut d'une session focus
 *   - L'activation globale de l'escalade automatique
 */
import { useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { RotateCcw, Save, Shield, Clock, UserCheck, Tag, Timer, AlertTriangle } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { useSettingsStore } from '@store/settingsStore';
import type { Quadrant } from '@store/types';

const QUADRANT_LABELS: Record<Quadrant, { title: string; color: string }> = {
  Q1: { title: 'Q1 · Urgent + Important', color: 'var(--color-urgent)' },
  Q2: { title: 'Q2 · Important non urgent', color: 'var(--color-important)' },
  Q3: { title: 'Q3 · Urgent non important', color: 'var(--color-normal)' },
  Q4: { title: 'Q4 · À déléguer', color: 'var(--color-defer)' },
};

export default function SettingsScreen(): JSX.Element {
  const settings = useSettingsStore();
  const [saved, setSaved] = useState(false);

  const showSavedToast = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1>Paramètres</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-medium)]">
            Personnalisez les règles et seuils qui pilotent UNIQUE.
          </p>
        </div>
        <UniqueButton
          variant="secondary"
          size="sm"
          leftIcon={<RotateCcw size={14} />}
          onClick={() => {
            if (confirm('Réinitialiser tous les paramètres aux valeurs par défaut ?')) {
              settings.resetToDefaults();
              showSavedToast();
            }
          }}
        >
          Réinitialiser
        </UniqueButton>
      </div>

      {saved && (
        <div className="unique-toast-success mb-4 text-sm font-semibold text-center animate-slide-up">
          ✦ Paramètres enregistrés
        </div>
      )}

      {/* ===== Délais d'escalade ===== */}
      <Section
        icon={<Shield size={18} />}
        title="Escalade automatique"
        subtitle="Nombre de jours de retard avant que la tâche soit automatiquement escaladée à votre N+1."
      >
        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="auto-escalation"
            checked={settings.autoEscalationEnabled}
            onChange={(e) => {
              settings.setAutoEscalationEnabled(e.target.checked);
              showSavedToast();
            }}
            className="w-4 h-4 accent-[color:var(--color-primary-dark)]"
          />
          <label htmlFor="auto-escalation" className="text-sm font-semibold cursor-pointer">
            Activer l'escalade automatique
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(Object.keys(QUADRANT_LABELS) as Quadrant[]).map((q) => (
            <DelayInput
              key={q}
              label={QUADRANT_LABELS[q].title}
              accent={QUADRANT_LABELS[q].color}
              value={settings.escalationDelays[q]}
              suffix="jours"
              onChange={(v) => {
                settings.updateEscalationDelay(q, v);
                showSavedToast();
              }}
            />
          ))}
        </div>
      </Section>

      {/* ===== Seuils d'urgence/importance ===== */}
      <Section
        icon={<Clock size={18} />}
        title="Seuils d'urgence et d'importance"
        subtitle="À partir de quelle échéance une tâche doit-elle être considérée comme urgente ou importante ?"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DelayInput
            label="Urgente si échéance ≤"
            accent="var(--color-urgent)"
            value={settings.urgencyThresholds.urgentWithinHours}
            suffix="heures"
            onChange={(v) => {
              settings.updateUrgencyThreshold('urgentWithinHours', v);
              showSavedToast();
            }}
          />
          <DelayInput
            label="Importante si échéance ≤"
            accent="var(--color-important)"
            value={settings.urgencyThresholds.importantWithinHours}
            suffix="heures"
            onChange={(v) => {
              settings.updateUrgencyThreshold('importantWithinHours', v);
              showSavedToast();
            }}
          />
        </div>
      </Section>

      {/* ===== Supérieur N+1 ===== */}
      <Section
        icon={<UserCheck size={18} />}
        title="Supérieur hiérarchique (N+1)"
        subtitle="Destinataire automatique des escalades. Les emails d'escalade seront envoyés à cette adresse."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextInput
            label="Nom complet"
            value={settings.supervisor.name}
            placeholder="ex : Marie Ouédraogo"
            onChange={(v) => {
              settings.updateSupervisor({ name: v });
              showSavedToast();
            }}
          />
          <TextInput
            label="Adresse email"
            type="email"
            value={settings.supervisor.email}
            placeholder="ex : directeur@entreprise.bf"
            onChange={(v) => {
              settings.updateSupervisor({ email: v });
              showSavedToast();
            }}
          />
        </div>
        {!settings.supervisor.email && settings.autoEscalationEnabled && (
          <div
            className="mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
            style={{
              background: 'rgba(239,83,80,0.08)',
              color: 'var(--color-urgent)',
              border: '1px solid rgba(239,83,80,0.3)',
            }}
          >
            <AlertTriangle size={14} />
            L'escalade automatique est activée mais aucun email N+1 n'est renseigné.
          </div>
        )}
      </Section>

      {/* ===== Mots-clés ===== */}
      <Section
        icon={<Tag size={18} />}
        title="Détection automatique depuis les emails"
        subtitle="Mots-clés qui font basculer une tâche détectée en urgente ou importante. Séparer par des virgules."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <KeywordsInput
            label="Mots-clés urgents"
            accent="var(--color-urgent)"
            value={settings.keywords.urgentKeywords}
            onChange={(v) => {
              settings.setKeywords('urgentKeywords', v);
              showSavedToast();
            }}
          />
          <KeywordsInput
            label="Mots-clés importants"
            accent="var(--color-important)"
            value={settings.keywords.importantKeywords}
            onChange={(v) => {
              settings.setKeywords('importantKeywords', v);
              showSavedToast();
            }}
          />
        </div>
      </Section>

      {/* ===== Focus par défaut ===== */}
      <Section
        icon={<Timer size={18} />}
        title="Session de focus"
        subtitle="Durée par défaut d'une session, quand la tâche n'a pas d'estimation explicite."
      >
        <DelayInput
          label="Durée par défaut"
          accent="var(--color-primary-dark)"
          value={settings.defaultFocusMinutes}
          suffix="minutes"
          onChange={(v) => {
            settings.setDefaultFocusMinutes(v);
            showSavedToast();
          }}
        />
      </Section>

      <div className="flex justify-end mt-6">
        <UniqueButton leftIcon={<Save size={16} />} onClick={showSavedToast}>
          Enregistrer (auto)
        </UniqueButton>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sous-composants                                                             */
/* -------------------------------------------------------------------------- */

function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: JSX.Element;
  title: string;
  subtitle: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <UniqueCard padding="lg" className="mb-5">
      <div className="flex items-start gap-3 mb-4">
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: 'var(--color-primary-light)',
            color: 'var(--color-primary-dark)',
          }}
        >
          {icon}
        </span>
        <div>
          <h3 className="text-base font-bold m-0">{title}</h3>
          <p className="text-xs text-[color:var(--color-text-medium)] mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </UniqueCard>
  );
}

function DelayInput({
  label,
  accent,
  value,
  suffix,
  onChange,
}: {
  label: string;
  accent: string;
  value: number;
  suffix: string;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold flex items-center gap-1.5" style={{ color: accent }}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: accent }} />
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="unique-input !pr-20"
        />
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold pointer-events-none"
          style={{ color: 'var(--color-text-medium)' }}
        >
          {suffix}
        </span>
      </div>
    </div>
  );
}

function TextInput({
  label,
  value,
  placeholder,
  type = 'text',
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  type?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="unique-input"
      />
    </div>
  );
}

function KeywordsInput({
  label,
  accent,
  value,
  onChange,
}: {
  label: string;
  accent: string;
  value: string[];
  onChange: (v: string[]) => void;
}): JSX.Element {
  const [text, setText] = useState(value.join(', '));
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold flex items-center gap-1.5" style={{ color: accent }}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: accent }} />
        {label}
      </label>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const parsed = text
            .split(',')
            .map((w) => w.trim())
            .filter(Boolean);
          onChange(parsed);
        }}
        placeholder="urgent, asap, immédiatement"
        className="unique-input"
      />
      <div className="flex flex-wrap gap-1 mt-1">
        {value.map((kw) => (
          <span
            key={kw}
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{
              background: 'var(--color-primary-light)',
              color: 'var(--color-primary-dark)',
              border: '1px solid var(--color-border)',
            }}
          >
            {kw}
          </span>
        ))}
      </div>
    </div>
  );
}
